// =============================================================================
// hopUpload.ts — shared upload helper for HOP assets (photos and documents).
//
// As of T-042 (migration 044), HOP assets live in Cloudflare R2 instead of
// Supabase Storage. The flow:
//   1. Watermark the photo client-side (unchanged from prior version).
//   2. Ask the api-gateway Worker for a presigned R2 upload URL.
//   3. PUT the bytes directly to R2 (zero data through the Worker).
//   4. Tell the Worker to write the hop_assets row.
// Read paths use the Worker's /hop/sign-download endpoint (presigned GET).
//
// Storage key shape: `hop/{pack_id}/{timestamp}-{safe_filename}.{ext}` in the
// `coretel-prod` R2 bucket. Same per-pack flat layout as before; export-time
// renaming (label_prefix + subcategory_code + instance_code + sequence) is
// still a render-time concern, the storage key is purely internal.
//
// 10 Oct 2026 (Nullarbor, photos going missing on one bar of signal):
//   * Every network step has a TIMEOUT and is aborted when it trips. Before,
//     a stalled request never failed — it hung, and the photo with it.
//   * capturedAtIso — a live capture is stamped with when it was TAKEN. It
//     used to be stamped with "now" at upload, so a photo queued on site and
//     sent from the roadhouse that night carried the roadhouse time.
//   * clientUploadId — a retry of the same photo reuses the same storage key
//     and the worker hands back the row it already wrote, so a lost reply on
//     a weak signal no longer makes a duplicate photo in the pack.
//
// 10 Oct 2026 (later, mig 441) — TWO COPIES. James: "always upload the best we
// can for us to review and then submit to client based on the acceptance
// requirement ... and have a fast reliable system for the field guys."
//   * uploadHopAsset now sends the QUICK copy: stamped, shrunk to just under
//     3,000,000 pixels (the cap in NBN-DEP-GDE-4266 r3.0), JPEG 0.85 — about
//     1 MB instead of 4–8. It is the hop_assets row and what every screen and
//     the client pack use. Its result says whether a full-size copy is owed.
//   * uploadHopFull sends the FULL-SIZE stamped copy afterwards and attaches
//     it to the same row (/hop/attach-full). lib/offline runs it from the
//     queue, only once no quick copy is waiting.
//   Pre-watermarked uploads, documents and photos already under the cap go up
//   once, as they are.
//
// 10 Oct 2026 (mig 442) — TILES. A 480-pixel copy (about 40 KB) goes up with
// every photo and is what the grids draw; "K Face" with 73 photos was pulling
// hundreds of megabytes to draw one screen. It rides on the quick copy's own
// sign-upload call and is strictly best-effort: a tile that does not make it
// never fails or slows the photo. getHopTiles signs a whole screen in one
// call; makeTilesForOlderPhotos gives photos from before today a tile.
// =============================================================================

import { apiFetch } from "@/lib/supabase";
import { watermarkPhoto } from "@/lib/watermark";
import { fetchHopPackAddressLines } from "@/lib/siteAddress";

// 10 Oct 2026 — run one network step with a deadline; abort it when it trips.
async function withDeadline<T>(ms: number, what: string, run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const ctl = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  // A true race: even a step that ignores the abort signal (the session
  // refresh inside apiFetch, say) cannot hold the photo past the deadline.
  const deadline = new Promise<never>((_, rej) => {
    timer = setTimeout(() => { ctl.abort(); rej(new Error(`${what} timed out — signal too weak`)); }, ms);
  });
  try {
    return await Promise.race([run(ctl.signal), deadline]);
  } catch (e: any) {
    if (ctl.signal.aborted) throw new Error(`${what} timed out — signal too weak`);
    throw e;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
// The PUT carries the photo itself: allow for a slow link (about 15 KB/s),
// never less than 2 minutes, never more than 10.
const putDeadlineMs = (bytes: number) => Math.min(600_000, Math.max(120_000, Math.round(bytes / 15)));

// =============================================================================
// T-199 — EXIF-first GPS extraction.
//
// Why: navigator.geolocation captures the user's location AT UPLOAD TIME.
// That's correct for fresh camera captures (capture ≈ upload time) but wrong
// for library uploads where the photo was taken hours/days/weeks earlier at
// a different location (e.g. uploading site photos from the office).
//
// Strategy: parse the photo's EXIF GPS first. If present (typical for any
// photo taken with location services on), use it — it's the actual capture
// location. Only fall back to navigator.geolocation if EXIF GPS is missing.
//
// We track the source on hop_assets.gps_source so reviewers can distinguish
// authoritative ('exif') from best-effort ('device_current') GPS.
// =============================================================================
type GpsSource = "exif" | "device_current";

interface GpsResult {
  lat: number;
  lng: number;
  source: GpsSource;
  /** Capture time from EXIF DateTimeOriginal, ISO format. Null if EXIF absent. */
  takenAtIso: string | null;
}

async function extractExifGps(file: File): Promise<{ lat: number; lng: number; takenAtIso: string | null } | null> {
  try {
    // Dynamic import keeps exifr out of the initial bundle (~20KB saved on
    // first load for users who never upload a HOP photo).
    const exifr = (await import("exifr")).default ?? (await import("exifr"));
    // Parse only the tags we care about — much faster than reading all EXIF.
    const result = await (exifr as any).parse(file, {
      pick: ["GPSLatitude", "GPSLongitude", "GPSLatitudeRef", "GPSLongitudeRef", "DateTimeOriginal"],
      gps: true,
    });
    if (!result) return null;
    const lat = typeof result.latitude === "number" ? result.latitude : null;
    const lng = typeof result.longitude === "number" ? result.longitude : null;
    if (lat == null || lng == null) return null;
    if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
    let takenAtIso: string | null = null;
    if (result.DateTimeOriginal instanceof Date && !isNaN(result.DateTimeOriginal.getTime())) {
      takenAtIso = result.DateTimeOriginal.toISOString();
    }
    return { lat, lng, takenAtIso };
  } catch (err) {
    // Non-image file, corrupt EXIF, library error — fall back silently.
    console.debug("[hopUpload] EXIF parse failed, falling back to device geolocation", err);
    return null;
  }
}

async function resolveGps(file: File, precomputedGps?: { lat: number; lng: number } | null): Promise<GpsResult | null> {
  // Caller supplied an already-resolved GPS (e.g. batch upload short-circuit).
  // T-217 strict rule (James 2026-05-07): only use device_current GPS / current
  // time when this is an IN-APP camera capture. precomputedGps being set means
  // the caller (in-app camera) has authoritative live-capture coordinates.
  // For library uploads (no precomputedGps), use EXIF only — never fall back
  // to device location or upload time. Missing data → blank, never wrong.
  if (precomputedGps !== undefined && precomputedGps !== null) {
    return { lat: precomputedGps.lat, lng: precomputedGps.lng, source: "device_current", takenAtIso: null };
  }
  // EXIF only — no device fallback for library uploads.
  const exif = await extractExifGps(file);
  if (exif) {
    return { lat: exif.lat, lng: exif.lng, source: "exif", takenAtIso: exif.takenAtIso };
  }
  // No EXIF, no precomputed → leave blank. Better than wrong.
  return null;
}

export type AssetType = "photo" | "document";

export interface HopUploadArgs {
  packId:               string;
  packCategoryId:       string;
  packSubcategoryId?:   string | null;
  instanceId?:          string | null;
  file:                 File;
  /** Pack label prefix from hop_packs.label_prefix — used in the watermark line */
  labelPrefix?:         string | null;
  /** Subcategory code (NBN-style: Construction_01, Sector_01, etc.) — for label */
  subcategoryCode?:     string | null;
  /** Instance code if applicable (S1, MW1, etc.) — for label */
  instanceCode?:        string | null;
  /** Optional human description for the asset */
  description?:         string | null;
  /** Employee uploading — used in audit + watermark */
  employeeId?:          string | null;
  employeeName?:        string | null;
  /** Skip GPS lookup (e.g. if already obtained for a batch) */
  precomputedGps?:      { lat: number; lng: number } | null;
  /** T-262 — photo is already watermarked externally (TimestampCamera, our
   *  rescue script). Upload the bytes as-is (no canvas roundtrip → existing
   *  watermark + EXIF preserved natively), but still extract EXIF GPS/datetime
   *  for the hop_assets row, and mark watermarked=true so the in-app CSS
   *  overlay won't double-render on top. */
  skipWatermark?:       boolean;
  /** 10 Oct 2026 — when a LIVE capture was taken (ISO). Stamped on the photo
   *  and the row instead of the upload time. Ignored for library uploads. */
  capturedAtIso?:       string | null;
  /** 10 Oct 2026 — one id per photo, minted at capture. Makes a retry safe. */
  clientUploadId?:      string | null;
  /** Progress callback for a batch upload */
  onProgress?:          (phase: "watermark" | "upload" | "save", pct: number) => void;
}

export interface HopUploadResult {
  asset_id:     string;
  storage_path: string;
  watermarked:  boolean;
  /** mig 441 — the quick copy was shrunk; the full-size copy is still to send. */
  full_pending: boolean;
}

/** mig 442 — the tile copy: longest edge and JPEG quality. */
const HOP_TILE_EDGE = 480;
const HOP_TILE_QUALITY = 0.7;
async function makeTile(src: Blob): Promise<Blob | null> {
  try {
    const url = URL.createObjectURL(src);
    const img = await new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; })
      .finally(() => URL.revokeObjectURL(url));
    const w0 = img.naturalWidth || img.width, h0 = img.naturalHeight || img.height;
    if (!w0 || !h0) return null;
    const k = Math.min(1, HOP_TILE_EDGE / Math.max(w0, h0));
    const cv = document.createElement("canvas");
    cv.width = Math.max(1, Math.round(w0 * k)); cv.height = Math.max(1, Math.round(h0 * k));
    const g = cv.getContext("2d"); if (!g) return null;
    g.drawImage(img, 0, 0, cv.width, cv.height);
    return await new Promise<Blob | null>(res => cv.toBlob(b => res(b), "image/jpeg", HOP_TILE_QUALITY));
  } catch { return null; }
}

/** mig 441 — the quick copy's pixel budget. NBN's cap is 3,000,000; a little
 *  under so rounding can never tip a photo over it. */
export const HOP_QUICK_MAX_PIXELS = 2_900_000;
const HOP_QUICK_QUALITY = 0.85;

function sniffAssetType(file: File): AssetType {
  if (file.type.startsWith("image/")) return "photo";
  return "document";
}

function buildLabel(args: HopUploadArgs): string | undefined {
  // Watermark line on the photo — keep it clean: just the site code (the
  // pack's label_prefix). Acceptance teams need to identify which site the
  // photo came from at a glance; the rest (subcategory + instance + sequence)
  // belongs in the photo LABEL / export FILENAME, not burned into every photo
  // as visual clutter. James 2026-04-25, re-confirmed 2026-07-30 (T-413 —
  // the Nyngan fix lives in the filename/label builders, NOT here).
  return args.labelPrefix ?? undefined;
}

// -----------------------------------------------------------------------------
// uploadHopAsset — public API. Same shape as before; backend swapped to R2.
// -----------------------------------------------------------------------------
export async function uploadHopAsset(args: HopUploadArgs): Promise<HopUploadResult> {
  const { packId, packCategoryId, file, onProgress } = args;
  const assetType = sniffAssetType(file);

  // 1. Process the file (watermark photos; documents pass through)
  let blobToUpload: Blob = file;
  let finalMime = file.type || "application/octet-stream";
  let watermarked = false;
  let gps: { lat: number; lng: number } | null = null;
  let gpsSource: GpsSource | null = null;     // T-199 — track EXIF vs device source
  let takenAt: string | null = null;
  let fullPending = false;                    // mig 441

  if (assetType === "photo") {
    onProgress?.("watermark", 0);

    // T-217 strict rule (James 2026-05-07): EXIF or blank, never upload-time
    // fallback for library uploads. precomputedGps means in-app camera capture
    // (live data is authoritative). Resolved.takenAtIso is EXIF DateTimeOriginal
    // when present.
    const resolved = await resolveGps(file, args.precomputedGps);
    if (resolved) {
      gps       = { lat: resolved.lat, lng: resolved.lng };
      gpsSource = resolved.source;
      // EXIF date OR null. In-app camera captures: use current time since
      // they ARE happening live (precomputedGps + we know capture is now).
      if (resolved.source === "device_current") {
        // in-app capture = live. 10 Oct 2026: the moment it was TAKEN when the
        // caller recorded it (a queued photo uploads hours later), else now.
        takenAt = args.capturedAtIso ?? new Date().toISOString();
      } else {
        takenAt = resolved.takenAtIso ?? null; // EXIF capture date or blank
      }
    } else if (args.precomputedGps !== undefined) {
      // T-262 — live capture intent but GPS failed (denied/timeout/etc).
      // HopCategoryScreen sets precomputedGps for live captures even when the
      // GPS lookup returned null, so the field being present (not undefined)
      // distinguishes a live capture from a library/document upload.
      // Capture time is unambiguously now; stamp it even without GPS.
      // GPS line correctly stays off (we never fake a location).
      takenAt = args.capturedAtIso ?? new Date().toISOString();
    } else {
      // No precomputedGps + no EXIF → leave both blank. Better than wrong.
      takenAt = null;
    }

    if (args.skipWatermark) {
      // T-262 — the bytes already carry a watermark (TimestampCamera, our
      // rescue script). resolveGps() ran above so gps/gpsSource/takenAt are
      // populated for the row from the photo's own EXIF. Upload as-is: no
      // canvas roundtrip means the existing watermark + EXIF are preserved
      // natively (no piexifjs needed). watermarked=true stops the in-app CSS
      // overlay from rendering on top of the burned-in one.
      blobToUpload = file;
      finalMime    = file.type || "image/jpeg";
      watermarked  = true;
    } else {
      // T-260 — reverse the T-217 clean-bytes regime. Burn a TimestampCamera-
      // style watermark into the JPEG so downloaded photos carry the date + GPS
      // visibly (matches Ventia Golden PP "visible on the photo and available
      // in metadata" requirement). The new watermarker also re-injects EXIF
      // DateTimeOriginal + GPS via piexifjs so the metadata side is satisfied.
      //
      // T-217 strict data rule is preserved end-to-end: takenAt + gps come from
      // EXIF (library upload) or precomputedGps + new Date() (in-app camera).
      // Missing data → omit the line. We never burn upload-time or device-now
      // fallbacks for library uploads.
      // T-260 Commit 2 — site address lines from projects.site_id → sites,
      // joined at upload time. Burned below the date/GPS so each photo carries
      // the registered street address (no reverse-geocoding, works offline).
      // (10 Oct 2026: this lookup gives up after 6 s and falls back to the
      // last good answer kept on the phone — see lib/siteAddress.)
      const addressLines = await fetchHopPackAddressLines(packId);

      const wmResult = await watermarkPhoto(file, {
        takenAt: takenAt ? new Date(takenAt) : null,
        gps,
        addressLines,
        maxPixels: HOP_QUICK_MAX_PIXELS,     // mig 441 — the quick copy
        quality:   HOP_QUICK_QUALITY,
      });
      blobToUpload = wmResult.blob;
      finalMime    = wmResult.mimeType;
      watermarked  = wmResult.watermarked;
      fullPending  = !!wmResult.shrunk;
    }

    onProgress?.("watermark", 100);
  }

  // 2. Ask the Worker for a presigned R2 PUT URL.
  const signRes = await withDeadline(30_000, "Starting the upload", signal => apiFetch("/hop/sign-upload", {
    method: "POST",
    signal,
    body: JSON.stringify({
      pack_id:      packId,
      file_name:    file.name,
      content_type: finalMime,
      client_upload_id: args.clientUploadId ?? undefined,
      with_thumb:   assetType === "photo" ? true : undefined,   // mig 442
    }),
  }));
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(`sign-upload failed (${signRes.status}): ${err.error ?? "unknown"}`);
  }
  const { upload_url, object_key, thumb_upload_url, thumb_object_key } = await signRes.json();

  // 3. PUT the bytes directly to R2 (zero data through Worker).
  onProgress?.("upload", 0);
  const putRes = await withDeadline(putDeadlineMs(blobToUpload.size), "Sending the photo", signal => fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": finalMime },
    body:    blobToUpload,
    signal,
  }));
  if (!putRes.ok) {
    throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);
  }
  onProgress?.("upload", 100);

  // 3b. mig 442 — the tile copy. Tiny, and never allowed to cost the photo:
  // 20 seconds at most, any failure just means this photo has no tile yet.
  let thumbKey: string | null = null;
  if (assetType === "photo" && thumb_upload_url && thumb_object_key) {
    try {
      const tile = await makeTile(blobToUpload);
      if (tile) {
        const tRes = await withDeadline(20_000, "Sending the tile", signal => fetch(thumb_upload_url, {
          method: "PUT", headers: { "Content-Type": "image/jpeg" }, body: tile, signal,
        }));
        if (tRes.ok) thumbKey = thumb_object_key;
      }
    } catch (e) { console.debug("[hopUpload] tile not sent — the photo is unaffected", e); }
  }

  // 4. Tell the Worker to write the hop_assets row.
  onProgress?.("save", 0);
  const finRes = await withDeadline(30_000, "Saving the photo to the pack", signal => apiFetch("/hop/finalise-asset", {
    method: "POST",
    signal,
    body: JSON.stringify({
      pack_id:             packId,
      pack_category_id:    packCategoryId,
      pack_subcategory_id: args.packSubcategoryId ?? null,
      instance_id:         args.instanceId ?? null,
      asset_type:          assetType,
      object_key,
      mime_type:           finalMime,
      file_size_bytes:     blobToUpload.size,
      file_name:           file.name,
      description:         args.description ?? null,
      gps_lat:             gps?.lat ?? null,
      gps_lng:             gps?.lng ?? null,
      gps_source:          gpsSource,            // T-199
      taken_at:            takenAt,
      watermarked,
      thumb_object_key:    thumbKey ?? undefined,     // mig 442
    }),
  }));
  if (!finRes.ok) {
    const err = await finRes.json().catch(() => ({}));
    // R2 object exists but DB insert failed — that's an orphan we'll clean
    // later via a cron sweep. User just retries the upload.
    throw new Error(`finalise-asset failed (${finRes.status}): ${err.error ?? "unknown"}${err.detail ? ` — ${err.detail}` : ""}`);
  }
  const { asset_id, storage_path } = await finRes.json();
  onProgress?.("save", 100);

  return { asset_id, storage_path, watermarked, full_pending: fullPending };
}

// -----------------------------------------------------------------------------
// uploadHopFull (mig 441) — the full-size stamped copy of a photo whose quick
// copy is already in Ops. Same stamp, same capture time and GPS (the args are
// the ones the quick copy used), full resolution. Returns "gone" when the
// photo was deleted in the office meanwhile — the caller stops trying.
// -----------------------------------------------------------------------------
export async function uploadHopFull(args: HopUploadArgs & { assetId: string }): Promise<"attached" | "gone"> {
  const { packId, file } = args;
  let gps: { lat: number; lng: number } | null = null;
  let takenAt: string | null = null;
  const resolved = await resolveGps(file, args.precomputedGps);
  if (resolved) {
    gps = { lat: resolved.lat, lng: resolved.lng };
    takenAt = resolved.source === "device_current"
      ? (args.capturedAtIso ?? new Date().toISOString())
      : (resolved.takenAtIso ?? null);
  } else if (args.precomputedGps !== undefined) {
    takenAt = args.capturedAtIso ?? new Date().toISOString();
  }
  const addressLines = await fetchHopPackAddressLines(packId);
  const wm = await watermarkPhoto(file, { takenAt: takenAt ? new Date(takenAt) : null, gps, addressLines });

  const signRes = await withDeadline(30_000, "Starting the full-size upload", signal => apiFetch("/hop/sign-upload", {
    method: "POST",
    signal,
    body: JSON.stringify({
      pack_id:      packId,
      file_name:    file.name,
      content_type: wm.mimeType,
      // its own stable key, next to the quick copy's
      client_upload_id: args.clientUploadId ? `${args.clientUploadId}_full` : undefined,
    }),
  }));
  if (!signRes.ok) {
    const err = await signRes.json().catch(() => ({}));
    throw new Error(`sign-upload failed (${signRes.status}): ${err.error ?? "unknown"}`);
  }
  const { upload_url, object_key } = await signRes.json();

  const putRes = await withDeadline(putDeadlineMs(wm.blob.size), "Sending the full-size photo", signal => fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": wm.mimeType },
    body:    wm.blob,
    signal,
  }));
  if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status} ${putRes.statusText}`);

  const attRes = await withDeadline(30_000, "Saving the full-size photo", signal => apiFetch("/hop/attach-full", {
    method: "POST",
    signal,
    body: JSON.stringify({ asset_id: args.assetId, object_key, file_size_bytes: wm.blob.size }),
  }));
  if (attRes.status === 410) return "gone";
  if (!attRes.ok) {
    const err = await attRes.json().catch(() => ({}));
    throw new Error(`attach-full failed (${attRes.status}): ${err.error ?? "unknown"}`);
  }
  return "attached";
}

// -----------------------------------------------------------------------------
// getAssetSignedUrl — presigned GET for one storage path. 30-min TTL.
// -----------------------------------------------------------------------------
export async function getAssetSignedUrl(
  storage_path: string,
  // ttlSeconds kept in signature for caller compat; Worker enforces 30 min.
  _ttlSeconds: number = 1800,
): Promise<string | null> {
  try {
    const res = await apiFetch("/hop/sign-download", {
      method: "POST",
      body: JSON.stringify({ storage_path }),
    });
    if (!res.ok) return null;
    const { download_url } = await res.json();
    return download_url ?? null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// getAssetSignedUrls — batch helper. Fan out N parallel requests.
// CF edge handles the load fine; signing is pure compute.
// -----------------------------------------------------------------------------
export async function getAssetSignedUrls(
  paths: string[],
  ttlSeconds: number = 1800,
): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map();
  const results = await Promise.all(
    paths.map(async (p) => [p, await getAssetSignedUrl(p, ttlSeconds)] as const),
  );
  const map = new Map<string, string>();
  for (const [p, url] of results) {
    if (url) map.set(p, url);
  }
  return map;
}

// -----------------------------------------------------------------------------
// getHopTiles (mig 442) — one call signs the tile for every photo on a screen
// (small tile copy where there is one, else the photo itself). `noTile` lists
// the photos that fell back. If the worker does not know the call yet, falls
// back to the old one-call-per-photo signing so the screen still loads.
// -----------------------------------------------------------------------------
export async function getHopTiles(
  assets: { asset_id: string; storage_path: string }[],
): Promise<{ urls: Map<string, string>; noTile: string[] }> {
  if (assets.length === 0) return { urls: new Map(), noTile: [] };
  try {
    const res = await apiFetch("/hop/sign-tiles", { method: "POST", body: JSON.stringify({ asset_ids: assets.map(a => a.asset_id) }) });
    if (res.ok) {
      const j = await res.json();
      return { urls: new Map(Object.entries(j.urls ?? {}) as [string, string][]), noTile: (j.no_tile ?? []) as string[] };
    }
  } catch { /* fall through */ }
  const byPath = await getAssetSignedUrls(assets.map(a => a.storage_path));
  const urls = new Map<string, string>();
  for (const a of assets) { const u = byPath.get(a.storage_path); if (u) urls.set(a.asset_id, u); }
  return { urls, noTile: [] };
}

// -----------------------------------------------------------------------------
// makeTilesForOlderPhotos (mig 442) — give photos from before today a tile.
// Runs in the browser of whoever pressed the button: download the photo, make
// the 480-pixel copy, upload it, attach it. Three at a time. A photo that
// fails is skipped (it keeps loading the old way) and counted.
// -----------------------------------------------------------------------------
export async function makeTilesForOlderPhotos(
  packId: string,
  assets: { asset_id: string; storage_path: string; file_name: string | null }[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ made: number; failed: number }> {
  let made = 0, failed = 0, done = 0, next = 0;
  const one = async (a: typeof assets[number]) => {
    try {
      const url = await getAssetSignedUrl(a.storage_path);
      if (!url) throw new Error("no url");
      const blob = await (await fetch(url)).blob();
      const tile = await makeTile(blob);
      if (!tile) throw new Error("no tile");
      const sign = await apiFetch("/hop/sign-upload", { method: "POST", body: JSON.stringify({
        pack_id: packId, file_name: a.file_name ?? "photo.jpg", content_type: "image/jpeg",
        client_upload_id: `tile_${a.asset_id.replace(/[^A-Za-z0-9]/g, "")}`,
      }) });
      if (!sign.ok) throw new Error("sign " + sign.status);
      const { upload_url, object_key } = await sign.json();
      const put = await fetch(upload_url, { method: "PUT", headers: { "Content-Type": "image/jpeg" }, body: tile });
      if (!put.ok) throw new Error("put " + put.status);
      const att = await apiFetch("/hop/attach-thumb", { method: "POST", body: JSON.stringify({ asset_id: a.asset_id, object_key }) });
      if (!att.ok) throw new Error("attach " + att.status);
      made++;
    } catch (e) { failed++; console.warn("[hop] tile not made for", a.asset_id, e); }
    onProgress?.(++done, assets.length);
  };
  const worker = async () => { while (next < assets.length) await one(assets[next++]); };
  await Promise.all([worker(), worker(), worker()]);
  return { made, failed };
}

// -----------------------------------------------------------------------------
// deleteHopAsset — removes from R2 + DB. RLS enforces capturer-self or mgr+.
// -----------------------------------------------------------------------------
export async function deleteHopAsset(asset_id: string): Promise<void> {
  const res = await apiFetch(`/hop/asset/${asset_id}`, { method: "DELETE" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`delete-asset failed (${res.status}): ${err.error ?? "unknown"}`);
  }
}
